import { Image as ImageLayer } from 'ol/layer'
import ImageWMS from 'ol/source/ImageWMS'
import useMap from 'hooks/use-map'
import { get } from 'es-toolkit/compat'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { transformExtent } from 'ol/proj'
import { buffer } from 'ol/extent'
import {
  geoserverLoadFunction,
  isGisProxyEnabled,
  wmsEndpointUrl,
} from 'common/geoserver'

// GeoServer draws cluster circles, counts and field names in pixels, and
// PointStacker moves clusters up to its cellSize (20px) off the fields. The
// layer extent therefore needs a margin in pixels too, or zoomed out it is only
// a few pixels wider than the fields and OpenLayers clips the symbols.
const EXTENT_MARGIN_PX = 100

export default function MapStaticBoundaryLayer(props) {
  const {
    fieldLayerName = STATIC_BOUNDARY_LAYER_NAME,
    mapRef = 'map',
    tenantId,
    layerZIndex,
  } = props

  const [params, setParams] = useState(null)

  let map = useMap(mapRef)

  const [source, setSource] = useState()
  const [layer, setLayer] = useState()
  const client = useSelector((state) => get(state, 'client.singleton'))
  const selectedYear = useSelector((state) => get(state, 'field.year'))
  const tenantExtent = useSelector((state) => get(state, 'field.tenantExtent'))

  useEffect(() => {
    if (tenantId && client && fieldLayerName !== '') {
      const scope = isGisProxyEnabled()
        ? // The proxy takes the tenant from the session and checks the client
          // belongs to it.
          { YEAR: selectedYear ?? '', CLIENT: client?.id ?? '' }
        : {
            viewparams: `tenant_id:${tenantId};year:${selectedYear ?? ''};client_id:${
              client?.id ?? ''
            }`,
          }
      setParams({
        tiled: false,
        SRS: 'EPSG:4326',
        TRANSPARENT: true,
        LAYERS: fieldLayerName,
        ...scope,
      })
    }
  }, [tenantId, client, selectedYear, fieldLayerName])

  useEffect(() => {
    if (params != null) {
      const _source = new ImageWMS({
        url: wmsEndpointUrl('boundaries'),
        serverType: 'geoserver',
        params: params,
        transition: 0,
        imageLoadFunction: geoserverLoadFunction,
      })
      setSource(_source)
    }
  }, [params])

  const fieldsExtent = useMemo(
    () =>
      tenantExtent
        ? transformExtent(tenantExtent, 'EPSG:4326', 'EPSG:3857')
        : undefined,
    [tenantExtent],
  )

  useEffect(() => {
    if (map && source) {
      const _layer = new ImageLayer({
        source,
        zIndex: layerZIndex,
      })
      setLayer(_layer)
    }
  }, [map, source])

  useEffect(() => {
    if (layer && map && fieldsExtent) {
      // The extent of every client's fields, not the selected client's: the
      // layer draws all clients' boundaries. OpenLayers crops the GetMap bbox
      // to it, skips the request when the view is outside it, and clips the
      // drawn image to it, so the pixel margin follows the zoom.
      const view = map.getView()
      const updateExtent = () => {
        layer.setExtent(
          buffer(fieldsExtent, view.getResolution() * EXTENT_MARGIN_PX),
        )
      }
      updateExtent()
      view.on('change:resolution', updateExtent)

      return () => {
        view.un('change:resolution', updateExtent)
        layer.setExtent(undefined)
      }
    }
  }, [layer, map, fieldsExtent])

  useEffect(() => {
    if (layer && map) {
      map.addLayer(layer)

      // Track zoom to hide layer during zoom animations
      const view = map.getView()
      let currentResolution = view.getResolution()
      let zoomTimeout = null

      const handleResolutionChange = () => {
        const newResolution = view.getResolution()

        // Only handle zoom, not pan
        if (newResolution !== currentResolution) {
          // Hide layer during zoom
          layer.setVisible(false)

          // Clear any existing timeout
          if (zoomTimeout) {
            clearTimeout(zoomTimeout)
          }

          // Show layer after zoom settles
          zoomTimeout = setTimeout(() => {
            layer.setVisible(true)
            currentResolution = newResolution
          }, 150)
        }
      }

      view.on('change:resolution', handleResolutionChange)

      return () => {
        if (zoomTimeout) {
          clearTimeout(zoomTimeout)
        }
        view.un('change:resolution', handleResolutionChange)
        map.removeLayer(layer)
      }
    }
  }, [layer, map])

  return null
}
